import React, { useState } from 'react';
import moment from 'moment';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Download, FileSpreadsheet } from 'lucide-react';

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

/**
 * Diálogo con 3 opciones de rango para el Reporte Maestro:
 * (a) Mes Específico, (b) Año Completo, (c) Todas las Cirugías.
 * Filtra por Fecha de la Cirugía (backend usa `surgeries.date`).
 */
export function MasterReportDialog({ open, onClose, onDownload }) {
  const now = moment();
  const [scope, setScope] = useState('month'); // 'month' | 'year' | 'all'
  const [year, setYear] = useState(String(now.year()));
  const [month, setMonth] = useState(String(now.month() + 1));
  const [downloading, setDownloading] = useState(false);

  const years = [];
  for (let y = now.year() + 1; y >= now.year() - 5; y--) years.push(y);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const params = {};
      if (scope === 'month') {
        const m = String(month).padStart(2, '0');
        params.start_date = `${year}-${m}-01`;
        params.end_date = moment(`${year}-${m}-01`).endOf('month').format('YYYY-MM-DD');
      } else if (scope === 'year') {
        params.start_date = `${year}-01-01`;
        params.end_date = `${year}-12-31`;
      }
      await onDownload?.(params);
      onClose?.();
    } finally {
      setDownloading(false);
    }
  };

  const scopeBtn = (val, label) => (
    <button
      type="button"
      onClick={() => setScope(val)}
      data-testid={`master-scope-${val}`}
      className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${
        scope === val
          ? 'border-[#2B4C3B] bg-[#EDF5F0] text-[#2B4C3B] font-medium'
          : 'border-[#E8E5DD] text-[#6A736B] hover:bg-[#FDFBF7]'
      }`}
    >
      {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid="master-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A1C18]">
            <FileSpreadsheet className="w-5 h-5 text-[#2B4C3B]" />
            Reporte Maestro
          </DialogTitle>
          <p className="text-xs text-[#6A736B]">
            Filtra por <strong>fecha de la cirugía</strong>. Todas las horas en horario Chile.
          </p>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Periodo</Label>
            <div className="flex gap-2 mt-1.5">
              {scopeBtn('month', 'Mes')}
              {scopeBtn('year', 'Año')}
              {scopeBtn('all', 'Todas')}
            </div>
          </div>

          {scope === 'month' && (
            <div className="grid grid-cols-2 gap-3" data-testid="master-month-fields">
              <div>
                <Label htmlFor="master-month">Mes</Label>
                <Select value={month} onValueChange={setMonth}>
                  <SelectTrigger id="master-month" data-testid="master-month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => (
                      <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="master-year-m">Año</Label>
                <Select value={year} onValueChange={setYear}>
                  <SelectTrigger id="master-year-m" data-testid="master-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {scope === 'year' && (
            <div data-testid="master-year-fields">
              <Label htmlFor="master-year-only">Año</Label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger id="master-year-only" data-testid="master-year-only-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {scope === 'all' && (
            <p className="text-sm text-[#6A736B] italic bg-[#FDFBF7] border border-[#E8E5DD] rounded-lg p-3">
              Se exportarán TODAS las cirugías registradas históricamente.
            </p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="master-cancel-btn">
            Cancelar
          </Button>
          <Button
            onClick={handleDownload}
            disabled={downloading}
            className="bg-[#2B4C3B] hover:bg-[#3A604D] text-white"
            data-testid="master-confirm-btn"
          >
            {downloading ? (
              <><Loader2 size={14} className="animate-spin mr-1.5" />Generando...</>
            ) : (
              <><Download size={14} className="mr-1.5" />Descargar</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
